import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import { Circle, StopCircle, Mic } from "lucide-react";
import axios from "axios";

const TranscriptionWidget = ({ onTranscriptionComplete }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [hasStartedRecording, setHasStartedRecording] = useState(false);
    const [transcription, setTranscription] = useState("");
    const [persistentLines, setPersistentLines] = useState([]);
    const [uploadStatus, setUploadStatus] = useState(""); // To track upload status
    const transcriptionRef = useRef(null);
    const transcriptionTextRef = useRef("");
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const intervalRef = useRef(null);
    const transcriptionEndRef = useRef(null);
    const API_HOST = process.env.REACT_APP_API_URL || "50.17.67.70";
    const RAG_UPLOAD_URL = "http://50.17.67.70:8009/upload-documents"
    const socketUrl = `http://${API_HOST}:5000`;
    const socketRef = useRef(null);
    const fileCountRef = useRef(0); // To keep track of file numbers

    useEffect(() => {
        if (transcriptionEndRef.current) {
            transcriptionEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [persistentLines, transcription]);

    useEffect(() => {
        socketRef.current = io(socketUrl, {
            path: '/socket.io',
            transports: ["websocket", "polling"],
            reconnectionAttempts: 5
        });
        const socket = socketRef.current;
        socket.on("transcription", (data) => {
            if (!data || !data.text) return;
            transcriptionTextRef.current += `\n${data.text}`;
            if (data.text.startsWith("##")) {
                setPersistentLines((prev) => [...prev, data.text]);
                setTranscription("");
            } else if (data.text.startsWith(">>")) {
                setTranscription(data.text);
            }
        });
        socket.on("connect_error", (error) => {
            console.error("❌ Socket connection error:", error);
        });
        return () => {
            socket.disconnect();
        };
    }, []);

    const handleStartRecording = async () => {
        if (isRecording) return;
        setIsRecording(true);
        setHasStartedRecording(true);
        setUploadStatus(""); // Clear previous upload status

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;

            // Load the audio worklet
            await audioContext.audioWorklet.addModule('/audio-processor.js');

            const source = audioContext.createMediaStreamSource(stream);
            const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

            workletNode.port.onmessage = (event) => {
                try {
                    const inputData = event.data.audioData;
                    console.log("Audio data received:", {
                            length: inputData.length,
                            sampleMin: Math.min(...inputData),
                            sampleMax: Math.max(...inputData),
                            sampleRate: audioContext.sampleRate
                        });
                    const originalSampleRate = audioContext.sampleRate;
                    const targetSampleRate = 16000;
                    const downsampled = downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
                    const pcmBuffer = floatTo16BitPCM(downsampled);
                    const binaryData = new Uint8Array(pcmBuffer);
                     console.log("Sending audio chunk:", {
                        originalLength: inputData.length,
                        downsampledLength: downsampled.length,
                        binaryLength: binaryData.length,
                        firstFewBytes: Array.from(binaryData.slice(0, 10))
                    });
                    socketRef.current.emit("audio_stream", binaryData);
                } catch (err) {
                    console.error("❌ Error during audio processing:", err);
                }
            };

            source.connect(workletNode);
            workletNode.connect(audioContext.destination);
            processorRef.current = workletNode;
        } catch (error) {
            console.error("❌ Error accessing microphone:", error);
            setIsRecording(false);
        }
        intervalRef.current = setInterval(() => {
            const currentText = transcriptionTextRef.current + (transcription || "");
            if (currentText.trim()) {
                socketRef.current.emit("context_update", { text: currentText });
            }

            if (onTranscriptionComplete) {
                onTranscriptionComplete(currentText);
            }

        }, 3000);
    };

    const downsampleBuffer = (buffer, sampleRate, targetSampleRate) => {
        if (targetSampleRate === sampleRate) return buffer;
        if (targetSampleRate > sampleRate) {
            console.error("Target sample rate must be lower than original sample rate");
            return buffer;
        }
        const sampleRateRatio = sampleRate / targetSampleRate;
        const newLength = Math.round(buffer.length / sampleRateRatio);
        const result = new Float32Array(newLength);
        let offsetResult = 0;
        let offsetBuffer = 0;
        while (offsetResult < newLength) {
            const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
            let accum = 0;
            let count = 0;
            for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
                accum += buffer[i];
                count++;
            }
            result[offsetResult] = count ? accum / count : 0;
            offsetResult++;
            offsetBuffer = nextOffsetBuffer;
        }
        return result;
    };

    const floatTo16BitPCM = (input) => {
        const output = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            let s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return output.buffer;
    };

    // Function to upload transcription to API
    const uploadTranscription = async (text) => {
        if (!text.trim()) {
            return;
        }

        try {
            // setUploadStatus("Uploading...");

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `transcription_${fileCountRef.current++}_${timestamp}.txt`;

            const file = new File([text], fileName, { type: 'text/plain' });

            // Create FormData object
            const formData = new FormData();
            formData.append('files', file);
            formData.append('collection_name', 'U00003');

            const response = await axios.post(RAG_UPLOAD_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setTimeout(() => setUploadStatus(""), 3000);

        } catch (error) {
            console.error("❌ Error uploading transcription:", error);
            if (error.response) {
                console.error("📡 Server response:", error.response.data);
                console.error("📡 Status code:", error.response.status);
            } else if (error.request) {
                console.error("📡 No response received:", error.request);
            } else {
                console.error("📡 Error setting up request:", error.message);
            }
            setUploadStatus("");
        }
    };

    const handleStopRecording = () => {
        if (!isRecording) return;
        setIsRecording(false);

        if (transcription.trim()) {
            transcriptionTextRef.current += `\n${transcription}`;
        }

        const finalText = transcriptionTextRef.current;

        if (onTranscriptionComplete) {
            onTranscriptionComplete(finalText);
        }

        uploadTranscription(finalText);

        transcriptionTextRef.current = "";

        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current.port.postMessage({ terminate: true });
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().then(() => console.log(""));
        }

        socketRef.current.emit("stop_audio");

        clearInterval(intervalRef.current);
        intervalRef.current = null;
    };

    useEffect(() => {
        if (transcriptionRef.current) {
            transcriptionRef.current.scrollTop = transcriptionRef.current.scrollHeight;
        }
    }, [transcription, persistentLines]);

    return (
        <div className="bg-white p-4 shadow-md shadow-blue-900/5 max-w-full relative rounded-xl">
            <h3 className="font-semibold my-2">Live Transcription</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 h-[200px] overflow-y-auto flex flex-col justify-between" ref={transcriptionRef}>
                <div className="flex-grow overflow-y-auto">
                    {persistentLines.map((line, index) => (
                        <p key={`persistent-${index} transcription`} className="my-1 leading-normal text-sm text-gray-700">
                            {line}
                        </p>
                    ))}
                    {transcription && <p key="interim transcription" className="my-1 leading-normal text-sm text-gray-700">{transcription}</p>}
                    {uploadStatus && (
                        <p className={`text-xs italic ${uploadStatus.includes("failed") ? "text-red-500" : "text-green-500"}`}>
                            {uploadStatus}
                        </p>
                    )}
                    <div ref={transcriptionEndRef} />
                </div>

                <div className="mt-3 flex justify-end">
                    <button
                        className={`flex items-center justify-center ${isRecording
                            ? "bg-red-100 text-red-700 hover:bg-red-200"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"} 
                            border-none rounded-md py-2 px-3 text-sm font-medium cursor-pointer transition-all duration-200`}
                        onClick={isRecording ? handleStopRecording : handleStartRecording}
                    >
                        {isRecording ? (
                            <>
                                <StopCircle size={16} className="mr-1.5" />
                                Stop
                            </>
                        ) : (
                            <>
                                <Mic size={16} className="mr-1.5" />
                                Record
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(TranscriptionWidget);